/**
 * PedidoDetalhesDialog - Modal unificado do pedido de compra com abas:
 * Detalhes | Financeiro | Anexos | Histórico.
 * Centraliza tudo que antes estava dividido entre "Detalhes" e "Financeiro".
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  CreditCard, TrendingUp, Eye, Download, RefreshCw, Save, MessageSquare,
  FileText, Paperclip, History, Send, Calendar, Package, ShoppingCart,
} from 'lucide-react';
import { PagamentoBadge, computePagamentoPedido } from './pagamentoStatus';
import { formatCurrency } from './utils';
import { getCurrencyMeta } from '../../lib/currencies';
import ParcelasForm from './ParcelasForm';
import AnexosManager from '../AnexosManager';

const PedidoDetalhesDialog = ({
  pedido,
  onClose,
  onAtualizarCustos,
  onPreviewPdf,
  onDownloadPdf,
  onSyncPagamento,
  onSaveRecado,
  onSaveTaxa,
  onAplicarDesconto,
  onRemoverDesconto,
  onSalvarParcelas,
  uploadAnexo,
  deleteAnexo,
  onAnexosChange,
  onAddComentario,
}) => {
  const fmt = (v) => formatCurrency(v, pedido?.moeda || 'BRL');
  const [recado, setRecado] = useState('');
  const [savingRecado, setSavingRecado] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [taxaCambio, setTaxaCambio] = useState('');
  const [descTipo, setDescTipo] = useState('valor');
  const [descValor, setDescValor] = useState('');
  const [descMotivo, setDescMotivo] = useState('');
  const [savingDesc, setSavingDesc] = useState(false);
  const [comentario, setComentario] = useState('');
  const [savingComentario, setSavingComentario] = useState(false);

  useEffect(() => {
    setRecado(pedido?.recado_financeiro || '');
    setTaxaCambio(pedido?.taxa_cambio != null ? String(pedido.taxa_cambio) : '');
  }, [pedido?.id, pedido?.recado_financeiro, pedido?.taxa_cambio]);

  const pag = pedido ? computePagamentoPedido(pedido) : null;

  const handleSync = async () => {
    if (!onSyncPagamento || !pedido) return;
    setSyncing(true);
    try { await onSyncPagamento(pedido); } finally { setSyncing(false); }
  };

  const handleSaveRecado = async () => {
    if (!onSaveRecado || !pedido) return;
    setSavingRecado(true);
    try { await onSaveRecado(pedido, recado); } finally { setSavingRecado(false); }
  };

  const handleAplicarDesconto = async () => {
    if (!onAplicarDesconto || !pedido) return;
    const v = parseFloat(descValor);
    if (!v || v <= 0) return;
    setSavingDesc(true);
    try {
      await onAplicarDesconto(pedido, { tipo: descTipo, valor: v, motivo: descMotivo });
      setDescValor(''); setDescMotivo('');
    } finally { setSavingDesc(false); }
  };

  const handleRemoverDesconto = async () => {
    if (!onRemoverDesconto || !pedido) return;
    setSavingDesc(true);
    try { await onRemoverDesconto(pedido); } finally { setSavingDesc(false); }
  };

  const handleAddComentario = async () => {
    if (!onAddComentario || !pedido || !comentario.trim()) return;
    setSavingComentario(true);
    try {
      await onAddComentario(pedido, comentario.trim());
      setComentario('');
    } finally { setSavingComentario(false); }
  };

  // Feed do histórico: comentários + histórico de status, ordenado por data desc
  const buildFeed = () => {
    const feed = [];
    (pedido?.comentarios || []).forEach((c) => {
      feed.push({
        tipo: 'comentario',
        setor: c.setor,
        autor: c.autor_nome,
        texto: c.texto,
        data: c.created_at,
      });
    });
    (pedido?.status_historico || []).forEach((h) => {
      feed.push({
        tipo: 'status',
        status: h.status,
        autor: h.usuario_nome,
        texto: h.observacao,
        data: h.data,
      });
    });
    return feed.sort((a, b) => new Date(b.data || 0) - new Date(a.data || 0));
  };

  const fmtDateTime = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('pt-BR'); } catch { return String(d); }
  };

  if (!pedido) return null;
  const feed = buildFeed();

  return (
    <Dialog open={!!pedido} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span>Pedido {pedido.numero}</span>
            {pedido.moeda && pedido.moeda !== 'BRL' && (
              <span className="text-xs px-2 py-0.5 rounded-full border border-indigo-300 text-indigo-700" data-testid="pedido-detalhe-moeda">
                {getCurrencyMeta(pedido.moeda).symbol} {pedido.moeda}
              </span>
            )}
            <Badge className={
              pedido.status === 'aprovado' ? 'bg-green-100 text-green-700' :
              pedido.status === 'pendente' ? 'bg-yellow-100 text-yellow-700' :
              pedido.status === 'enviado' ? 'bg-blue-100 text-blue-700' :
              pedido.status === 'recebido' ? 'bg-green-100 text-green-700' :
              'bg-gray-100 text-gray-700'
            }>
              {pedido.status?.charAt(0).toUpperCase() + pedido.status?.slice(1)}
            </Badge>
            {pag && <PagamentoBadge pedido={pedido} />}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="detalhes" className="w-full mt-2">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="detalhes" data-testid="tab-pedido-detalhes">
              <FileText className="w-4 h-4 mr-1" /> Detalhes
            </TabsTrigger>
            <TabsTrigger value="financeiro" data-testid="tab-pedido-financeiro">
              <CreditCard className="w-4 h-4 mr-1" /> Financeiro
            </TabsTrigger>
            <TabsTrigger value="anexos" data-testid="tab-pedido-anexos">
              <Paperclip className="w-4 h-4 mr-1" /> Anexos
              {pedido.anexos?.length > 0 && ` (${pedido.anexos.length})`}
            </TabsTrigger>
            <TabsTrigger value="historico" data-testid="tab-pedido-historico">
              <History className="w-4 h-4 mr-1" /> Histórico
            </TabsTrigger>
          </TabsList>

          {/* ============ ABA DETALHES ============ */}
          <TabsContent value="detalhes" className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Fornecedor</p>
                <p className="font-medium">{pedido.fornecedor_nome}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Data do Pedido</p>
                <p className="font-medium">{new Date(pedido.created_at).toLocaleDateString('pt-BR')}</p>
              </div>
              {pedido.titulo && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Título</p>
                  <p className="font-medium">{pedido.titulo}</p>
                </div>
              )}
              {pedido.projeto_nome && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Projeto</p>
                  <p className="font-medium">{pedido.projeto_nome}</p>
                </div>
              )}
            </div>

            <div>
              <h4 className="font-medium mb-3">Itens do Pedido</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3">Item</th>
                      <th className="text-center p-3">Qtd</th>
                      <th className="text-right p-3">Preço Unit.</th>
                      <th className="text-right p-3">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedido.itens?.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-3"><p className="font-medium">{item.descricao}</p></td>
                        <td className="p-3 text-center">{item.quantidade} {item.unidade}</td>
                        <td className="p-3 text-right">{fmt(item.preco_unitario || 0)}</td>
                        <td className="p-3 text-right font-medium">{fmt((item.preco_unitario || 0) * item.quantidade)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-medium">
                    <tr className="border-t-2">
                      <td colSpan={3} className="p-3 text-right">Total:</td>
                      <td className="p-3 text-right text-lg text-green-600">{fmt(pedido.total || 0)}</td>
                    </tr>
                    {pedido.moeda && pedido.moeda !== 'BRL' && pedido.taxa_cambio > 0 && (
                      <tr>
                        <td colSpan={3} className="p-2 text-right text-xs text-gray-500">Equivalente em R$ (gerencial):</td>
                        <td className="p-2 text-right text-sm text-gray-600" data-testid="pedido-detalhe-equivalente">
                          ≈ {formatCurrency((pedido.total || 0) * pedido.taxa_cambio, 'BRL')}
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Desconto adicional */}
            <div className="border rounded-lg p-4 bg-amber-50/50 border-amber-200" data-testid="pedido-desconto-adicional">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-amber-900">Desconto adicional</p>
                {Number(pedido.desconto_adicional) > 0 && (
                  <span className="text-sm font-semibold text-rose-700" data-testid="pedido-desconto-adicional-valor">
                    −{fmt(pedido.desconto_adicional)}
                    {pedido.desconto_adicional_tipo === 'percentual' && pedido.desconto_adicional_input
                      ? ` (${pedido.desconto_adicional_input}%)` : ''}
                  </span>
                )}
              </div>
              {Number(pedido.desconto_adicional) > 0 && pedido.desconto_adicional_motivo && (
                <p className="text-xs text-gray-600 mb-2">Motivo: {pedido.desconto_adicional_motivo}</p>
              )}
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex rounded-md overflow-hidden border border-amber-300">
                  <button type="button"
                    className={`px-3 py-1.5 text-sm ${descTipo === 'valor' ? 'bg-amber-600 text-white' : 'bg-white text-amber-800'}`}
                    onClick={() => setDescTipo('valor')} data-testid="btn-desconto-tipo-valor">R$</button>
                  <button type="button"
                    className={`px-3 py-1.5 text-sm ${descTipo === 'percentual' ? 'bg-amber-600 text-white' : 'bg-white text-amber-800'}`}
                    onClick={() => setDescTipo('percentual')} data-testid="btn-desconto-tipo-percentual">%</button>
                </div>
                <div className="w-28">
                  <Input type="number" min="0" step="0.01" value={descValor}
                    onChange={(e) => setDescValor(e.target.value)}
                    placeholder={descTipo === 'percentual' ? '% ex: 5' : 'R$ ex: 250'}
                    data-testid="input-desconto-valor" />
                </div>
                <div className="flex-1 min-w-[160px]">
                  <Input value={descMotivo} onChange={(e) => setDescMotivo(e.target.value)}
                    placeholder="Motivo (opcional)" data-testid="input-desconto-motivo" />
                </div>
                <Button type="button" size="sm" className="bg-amber-600 hover:bg-amber-700"
                  onClick={handleAplicarDesconto} disabled={savingDesc || !descValor}
                  data-testid="btn-aplicar-desconto">
                  {savingDesc ? 'Aplicando...' : 'Aplicar'}
                </Button>
                {Number(pedido.desconto_adicional) > 0 && (
                  <Button type="button" size="sm" variant="outline"
                    onClick={handleRemoverDesconto} disabled={savingDesc} data-testid="btn-remover-desconto">
                    Remover
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">Recalcula o total do pedido e registra o histórico. Permitido em qualquer status.</p>
            </div>

            {/* Câmbio gerencial */}
            {pedido.moeda && pedido.moeda !== 'BRL' && (
              <div className="flex flex-wrap items-end gap-3 p-3 bg-indigo-50 border border-indigo-100 rounded-lg" data-testid="pedido-taxa-cambio-box">
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Taxa de câmbio — 1 {pedido.moeda} = ? R$</label>
                  <Input type="number" step="0.0001" min="0" value={taxaCambio}
                    onChange={(e) => setTaxaCambio(e.target.value)} placeholder="Ex: 5.30"
                    className="w-40 bg-white" data-testid="pedido-input-taxa-cambio" />
                </div>
                <Button size="sm" variant="outline"
                  onClick={() => onSaveTaxa?.(pedido, taxaCambio === '' ? null : parseFloat(taxaCambio))}
                  data-testid="pedido-salvar-taxa-cambio">
                  Salvar taxa
                </Button>
                <p className="text-xs text-gray-500 flex-1 min-w-[200px]">
                  Não altera os valores — usada só para exibir o equivalente em R$ e no total gerencial do dashboard.
                  {pedido.taxa_cambio_data && (
                    <span className="block mt-1 text-emerald-700 font-medium" data-testid="pedido-taxa-travada">
                      🔒 Taxa travada na emissão em {new Date(pedido.taxa_cambio_data).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Condições Comerciais */}
            {(pedido.cotacao_origem_id || pedido.cotacao_id || pedido.condicao_pagamento || pedido.prazo_entrega || pedido.prazo_garantia) && (
              <div className="space-y-2" data-testid="pedido-condicoes-detalhes">
                <h4 className="font-medium text-sm text-gray-700">Condições Comerciais</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 bg-gray-50 rounded-lg text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Condição de Pagamento</p>
                    <p className="font-medium">{pedido.condicao_pagamento || <span className="text-gray-400 italic">—</span>}</p>
                  </div>
                  {pedido.forma_pagamento && (
                    <div data-testid="pedido-forma-pagamento">
                      <p className="text-xs text-gray-500">Forma de Pagamento</p>
                      <p className="font-medium">{pedido.forma_pagamento}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-500">Prazo de Entrega</p>
                    <p className="font-medium">{pedido.prazo_entrega || <span className="text-gray-400 italic">—</span>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Garantia</p>
                    <p className="font-medium">{pedido.prazo_garantia || <span className="text-gray-400 italic">—</span>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Assistência Técnica</p>
                    <p className="font-medium">{pedido.assistencia_tecnica || <span className="text-gray-400 italic">—</span>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Frete</p>
                    <p className="font-medium">
                      {pedido.frete_tipo === 'cif'
                        ? 'CIF (incluso)'
                        : pedido.frete_tipo === 'fob'
                          ? `FOB ${(pedido.frete_valor || pedido.frete) ? '· ' + (pedido.frete_valor || pedido.frete).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : ''}`
                          : pedido.frete > 0
                            ? pedido.frete.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                            : <span className="text-gray-400 italic">—</span>}
                    </p>
                  </div>
                  {Number(pedido.frete_minimo) > 0 && (
                    <div>
                      <p className="text-xs text-gray-500">Frete Mínimo</p>
                      <p className="font-medium text-amber-700">{Number(pedido.frete_minimo).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                    </div>
                  )}
                  {Number(pedido.valor_impostos) > 0 && (
                    <div>
                      <p className="text-xs text-gray-500">Impostos</p>
                      <p className="font-medium">
                        {Number(pedido.valor_impostos).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}{' '}
                        {pedido.impostos_inclusos && <span className="text-[10px] text-gray-400">(inclusos)</span>}
                      </p>
                    </div>
                  )}
                  {Number(pedido.valor_desconto) > 0 && (
                    <div>
                      <p className="text-xs text-gray-500">Desconto</p>
                      <p className="font-medium text-rose-700">−{Number(pedido.valor_desconto).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                    </div>
                  )}
                  {pedido.incoterm && (
                    <div data-testid="pedido-incoterm">
                      <p className="text-xs text-gray-500">Incoterm</p>
                      <p className="font-medium uppercase">{pedido.incoterm}</p>
                    </div>
                  )}
                  {pedido.porto_origem && (
                    <div data-testid="pedido-porto-origem">
                      <p className="text-xs text-gray-500">Porto / Origem</p>
                      <p className="font-medium">{pedido.porto_origem}</p>
                    </div>
                  )}
                  {pedido.transit_time_dias > 0 && (
                    <div data-testid="pedido-transit-time">
                      <p className="text-xs text-gray-500">Transit Time</p>
                      <p className="font-medium">{pedido.transit_time_dias} dia(s)</p>
                    </div>
                  )}
                  {pedido.peso_bruto > 0 && (
                    <div data-testid="pedido-peso-bruto">
                      <p className="text-xs text-gray-500">Peso Bruto</p>
                      <p className="font-medium">{Number(pedido.peso_bruto).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kg</p>
                    </div>
                  )}
                  {pedido.peso_liquido > 0 && (
                    <div data-testid="pedido-peso-liquido">
                      <p className="text-xs text-gray-500">Peso Líquido</p>
                      <p className="font-medium">{Number(pedido.peso_liquido).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kg</p>
                    </div>
                  )}
                  {pedido.quantidade_volumes > 0 && (
                    <div data-testid="pedido-volumes">
                      <p className="text-xs text-gray-500">Volumes</p>
                      <p className="font-medium">{pedido.quantidade_volumes}</p>
                    </div>
                  )}
                  {pedido.volume_cbm > 0 && (
                    <div data-testid="pedido-cbm">
                      <p className="text-xs text-gray-500">Volume (CBM)</p>
                      <p className="font-medium">{Number(pedido.volume_cbm).toLocaleString('pt-BR', { maximumFractionDigits: 4 })} m³</p>
                    </div>
                  )}
                </div>
                {pedido.observacao_fornecedor && (
                  <div className="p-3 bg-blue-50/50 border border-blue-200 rounded-lg" data-testid="pedido-obs-fornecedor">
                    <p className="text-xs text-blue-700 mb-1 font-medium">Observação do Fornecedor</p>
                    <p className="text-sm text-slate-700 italic">&ldquo;{pedido.observacao_fornecedor}&rdquo;</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* ============ ABA FINANCEIRO ============ */}
          <TabsContent value="financeiro" className="space-y-5 mt-4">
            {/* Status Financeiro (Conta Azul) + Recado */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="pedido-financeiro-box">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Status Financeiro</span>
                  {pag && <PagamentoBadge pedido={pedido} />}
                </div>
                {onSyncPagamento && (
                  <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}
                    data-testid="btn-sync-pagamento" title="Puxar o status de pagamento (baixa) do Conta Azul">
                    <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Atualizando...' : 'Atualizar do Conta Azul'}
                  </Button>
                )}
              </div>
              {pag && <p className="text-sm text-slate-600" data-testid="pedido-financeiro-resumo">{pag.resumo}</p>}
              {/* Barra pago vs em aberto */}
              {pag && pag.nTotal > 0 && (
                <div className="space-y-1" data-testid="pedido-pagamento-barra">
                  <div className="flex justify-between text-xs">
                    <span className="text-emerald-700 font-medium">Pago: {formatCurrency(pag.pago, 'BRL')}</span>
                    <span className="text-amber-700 font-medium">Em aberto: {formatCurrency(Math.max((pag.total || 0) - (pag.pago || 0), 0), 'BRL')}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-amber-200 overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${pag.total > 0 ? Math.min(100, (pag.pago / pag.total) * 100) : 0}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-slate-500">{pag.nPagas}/{pag.nTotal} parcela(s) paga(s)</p>
                </div>
              )}
              {pedido.pagamento_last_sync && (
                <p className="text-[11px] text-slate-400">
                  Última consulta ao Conta Azul: {new Date(pedido.pagamento_last_sync).toLocaleString('pt-BR')}
                </p>
              )}
              <p className="text-[11px] text-emerald-600" data-testid="pedido-auto-sync-tag">
                ⟳ Sincronização automática do Conta Azul às 08h e 15h (Brasília)
              </p>
              <div className="pt-2 border-t border-slate-200">
                <div className="flex items-center gap-1.5 mb-1">
                  <MessageSquare className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-medium text-slate-700">Recado do Financeiro</span>
                  {pedido.recado_financeiro_by && (
                    <span className="text-[11px] text-slate-400">— por {pedido.recado_financeiro_by}</span>
                  )}
                </div>
                <Textarea value={recado} onChange={(e) => setRecado(e.target.value)}
                  placeholder="Ex.: NF em análise, aguardando boleto do fornecedor, entrada agendada para 10/07..."
                  rows={2} className="text-sm bg-white" data-testid="input-recado-financeiro" />
                <div className="flex justify-end mt-2">
                  <Button size="sm" variant="outline" onClick={handleSaveRecado}
                    disabled={savingRecado || recado === (pedido.recado_financeiro || '')}
                    data-testid="btn-salvar-recado">
                    <Save className="w-4 h-4 mr-1" />
                    {savingRecado ? 'Salvando...' : 'Salvar recado'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Parcelas (editável) */}
            <div>
              <h4 className="font-medium flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4" /> Parcelas de Pagamento
              </h4>
              {onSalvarParcelas ? (
                <ParcelasForm
                  pedido={pedido}
                  onSave={(parcelas, observacao, opts) => onSalvarParcelas(pedido.id, parcelas, observacao, opts)}
                  onCancel={() => {}}
                />
              ) : (
                <p className="text-sm text-gray-400">Sem parcelas definidas.</p>
              )}
            </div>
          </TabsContent>

          {/* ============ ABA ANEXOS ============ */}
          <TabsContent value="anexos" className="mt-4">
            {uploadAnexo ? (
              <AnexosManager
                anexos={pedido.anexos || []}
                onChange={(novosAnexos) => onAnexosChange?.(pedido, novosAnexos)}
                showLabel={false}
                uploadHandler={uploadAnexo}
                deleteHandler={deleteAnexo}
              />
            ) : (
              <p className="text-sm text-gray-400">Nenhum anexo.</p>
            )}
          </TabsContent>

          {/* ============ ABA HISTÓRICO ============ */}
          <TabsContent value="historico" className="space-y-4 mt-4">
            {/* Novo comentário */}
            <div className="rounded-lg border border-slate-200 p-3 bg-white" data-testid="pedido-comentario-box">
              <div className="flex items-center gap-1.5 mb-2">
                <MessageSquare className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-slate-700">Adicionar comentário</span>
              </div>
              <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)}
                placeholder="Comente sobre pagamento, negociação, observações internas..."
                rows={2} className="text-sm" data-testid="input-comentario-pedido" />
              <div className="flex justify-end mt-2">
                <Button size="sm" onClick={handleAddComentario}
                  disabled={savingComentario || !comentario.trim()}
                  data-testid="btn-add-comentario">
                  <Send className="w-4 h-4 mr-1" />
                  {savingComentario ? 'Enviando...' : 'Comentar'}
                </Button>
              </div>
            </div>

            {/* Feed */}
            {feed.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">Nenhum registro ainda.</p>
            ) : (
              <div className="space-y-2" data-testid="pedido-historico-feed">
                {feed.map((f, idx) => (
                  <div key={idx} className={`rounded-lg border p-3 ${
                    f.tipo === 'comentario'
                      ? (f.setor === 'financeiro' ? 'border-blue-200 bg-blue-50/40' : 'border-purple-200 bg-purple-50/40')
                      : 'border-slate-200 bg-slate-50/40'
                  }`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        {f.tipo === 'comentario' ? (
                          <Badge className={f.setor === 'financeiro' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                            {f.setor === 'financeiro' ? 'Financeiro' : 'Compras'}
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-200 text-slate-700 flex items-center gap-1">
                            <Package className="w-3 h-3" /> {f.status || 'status'}
                          </Badge>
                        )}
                        <span className="text-xs font-medium text-slate-600">{f.autor || '—'}</span>
                      </div>
                      <span className="text-[11px] text-slate-400">{fmtDateTime(f.data)}</span>
                    </div>
                    {f.texto && <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap">{f.texto}</p>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onClose()}>Fechar</Button>
          {pedido?.projeto_id && (
            <Button variant="outline" onClick={() => pedido && onAtualizarCustos(pedido)}
              title="Conferir e distribuir custos na Ficha Técnica do projeto"
              data-testid="btn-atualizar-custos">
              <TrendingUp className="w-4 h-4 mr-1" /> Conferir Custos do Projeto
            </Button>
          )}
          <Button variant="outline" onClick={() => pedido && onPreviewPdf('pedido', pedido.id, pedido.numero)}
            data-testid="btn-preview-pedido-modal">
            <Eye className="w-4 h-4 mr-1" /> Visualizar PDF
          </Button>
          <Button onClick={() => pedido && onDownloadPdf(pedido.id, pedido.numero)}
            data-testid="btn-pdf-pedido-modal">
            <Download className="w-4 h-4 mr-1" /> Baixar PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PedidoDetalhesDialog;
